import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { 
  Plus, Search, Filter, Calendar, BookOpen, Users, Clock, 
  CheckCircle, AlertCircle, Eye, Edit, Trash2, 
  MessageSquare, ChevronLeft, ChevronRight, BarChart3, 
  FileText, Tag, Activity, ChevronDown, LayoutGrid, List,
  Settings, Target
} from 'lucide-react';
import { useData } from '../../contexts/DataContext';
import EXAM_API from '../../api/services/examApi';
import ConfirmationModal from '../../ui/ConfirmationModal';
import WhatsAppNoticeModal from '../../components/Exams/WhatsAppNoticeModal';
import SettingsHeader from '../../ui/SettingsHeader';
import Loader from '../../ui/Loader';

import ScheduleNoticeModal from '../../components/Exams/ScheduleNoticeModal';


const PAGE_SIZE = 12;

const ExamListView = ({ onCreateExam, onEditExam, onViewExamDetails }) => {
  const { classes: apiClasses, currentAcademicYear } = useData();
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  
  const [filters, setFilters] = useState({
    search: '',
    class: '',
    section: '',
    examType: '',
    status: '',
    academicYear: currentAcademicYear || ''
  });
  
  const [currentPage, setCurrentPage] = useState(1);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExam, setSelectedExam] = useState(null);
  
  // Schedule Modal State
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleExam, setScheduleExam] = useState(null);



  const loadExams = async () => {
    setLoading(true);
    try {
      const response = await EXAM_API.getAllExams({ academicYear: filters.academicYear });
      setExams(response.data?.exams || response.data || []);
    } catch (error) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const handleSendSchedule = (exam) => {
    setScheduleExam(exam);
    setShowScheduleModal(true);
  };



  useEffect(() => {
    loadExams();
  }, [filters.academicYear, currentAcademicYear]);

  const filteredExams = useMemo(() => {
    return exams.filter(exam => {
      if (filters.search && !exam.examName.toLowerCase().includes(filters.search.toLowerCase())) return false;
      if (filters.class) {
         const match = (exam.classes || []).find(c => {
            const classId = c.unitId?._id || c.unitId || c.id || '';
            return classId.toString() === filters.class;
         });
         if (!match) return false;
      }
      if (filters.examType && exam.examType !== filters.examType) return false;
      if (filters.status && exam.status !== filters.status) return false;
      return true;
    });
  }, [exams, filters]);

  const paginatedExams = filteredExams.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const getStatusBadge = (status) => {
    const styles = {
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      ongoing: "bg-amber-50 text-amber-700 border-amber-200",
      scheduled: "bg-blue-50 text-blue-700 border-blue-200",
      default: "bg-gray-50 text-gray-700 border-gray-200"
    };
    return styles[status] || styles.default;
  };

  return (
    <div className="space-y-6">
      <SettingsHeader
        title="Examinations"
        icon={FileText}
        buttons={[
          {
            label: "Create New Exam",
            icon: Plus,
            onClick: onCreateExam,
            variant: "primary",
          },
        ]}
      >
        <div className="flex-1 flex items-center gap-3 overflow-x-auto no-scrollbar">
          <div className="relative min-w-[180px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name..."
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-4 py-2 text-sm bg-white border border-gray-300 rounded-md outline-none focus:border-[var(--primary-color)] focus:ring-1 focus:ring-[var(--primary-color)] transition-all"
            />
          </div>

          <div className="relative min-w-[140px]">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <select
              value={filters.class}
              onChange={(e) => setFilters(prev => ({ ...prev, class: e.target.value }))}
              className="w-full pl-10 pr-8 py-2 text-sm bg-white border border-gray-300 rounded-md appearance-none cursor-pointer outline-none focus:border-[var(--primary-color)] focus:ring-1 focus:ring-[var(--primary-color)]"
            >
              <option value="">All Classes</option>
              {apiClasses.map(cls => <option key={cls._id} value={cls._id}>{cls.unitName}</option>)}
            </select>
          </div>

          <div className="flex bg-gray-100 p-1 rounded-md ml-auto">
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-all ${viewMode === 'grid' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}
            >
              <LayoutGrid size={16} />
            </button>
            <button 
              onClick={() => setViewMode('list')}
              className={`p-1.5 rounded transition-all ${viewMode === 'list' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-400'}`}
            >
              <List size={16} />
            </button>
          </div>
        </div>
      </SettingsHeader>

      {/* STATS SECTION */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Registered Exams', value: exams.length, icon: FileText },
          { label: 'Upcoming', value: exams.filter(e => e.status === 'scheduled').length, icon: Calendar },
          { label: 'Completed', value: exams.filter(e => e.status === 'completed').length, icon: CheckCircle }
        ].map((stat, i) => (
          <div key={i} className="bg-white p-5 rounded-lg border border-gray-200 shadow-sm flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
            </div>
            <div className="p-3 rounded-md bg-gray-50 text-gray-600">
              <stat.icon size={20} />
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <Loader />
      ) : filteredExams.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-16 text-center shadow-sm">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No examinations found</h3>
          <p className="text-sm text-gray-500 max-w-sm mx-auto">Create your first exam to get started with the examination management system</p>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" : "space-y-3"}>
          {paginatedExams.map((exam) => {
            const allSubjects = exam.classes?.flatMap(cls => cls.subjects || []) || [];
            const classNames = exam.classes?.map(c => c.unitId?.unitName || c.unitName).filter(Boolean).join(', ') || 'N/A';
            const totalMarks = allSubjects.reduce((sum, s) => sum + (s.totalMarks || 0), 0);
            const firstSubject = allSubjects[0];

            return (
              <div key={exam._id} className="bg-white border border-gray-200 rounded-lg p-5 shadow-sm hover:shadow-md transition-all group">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`px-2.5 py-1 rounded-md text-xs font-medium border ${getStatusBadge(exam.status)}`}>
                        {exam.status || 'SCHEDULED'}
                      </span>
                      <span className="text-xs text-gray-500 uppercase">{exam.examType}</span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 line-clamp-1 group-hover:text-[var(--primary-color)] transition-colors">
                      {exam.examName}
                    </h3>
                    {(exam.status === 'scheduled' || !exam.status) && firstSubject?.startTime && (
                         <div className="flex items-center gap-1.5 mt-1 text-xs font-medium text-gray-500">
                            <Clock size={12} className="text-[var(--primary-color)]" />
                            <span>
                               {new Date(firstSubject.examDate).toLocaleDateString()} @ {firstSubject.startTime}
                            </span>
                         </div>
                    )}
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => onEditExam(exam)} className="p-2 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-all">
                      <Edit size={14} />
                    </button>
                    <button onClick={() => { setSelectedExam(exam); setShowDeleteModal(true); }} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-all">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 py-4 border-t border-b border-gray-100 mb-4">
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Enrolled Classes</label>
                    <p className="text-sm font-medium text-gray-900 truncate">{classNames}</p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 uppercase block mb-1">Total Marks</label>
                    <p className="text-sm font-semibold text-gray-900">{totalMarks}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <BookOpen size={14} className="text-gray-400" />
                    <span className="text-sm font-medium text-gray-600">{allSubjects.length} Subjects</span>
                  </div>
                  <div className="flex items-center">
                    <button 
                      onClick={() => onViewExamDetails(exam)}
                      className="px-4 py-2 text-white rounded-md text-xs font-medium hover:opacity-90 transition-all shadow-sm w-full"
                      style={{ backgroundColor: 'var(--primary-color)' }}
                    >
                      View Record
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {showDeleteModal && (
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
             try {
                await EXAM_API.deleteExam(selectedExam._id);
                loadExams();
                setShowDeleteModal(false);
                toast.success('Exam deleted');
             } catch(e) { toast.error('Failed to delete'); }
          }}
          title="Archive Examination"
          message={`Are you sure you want to archive ${selectedExam?.examName}? This action can be reversed in audit logs.`}
        />
      )}

      {showScheduleModal && (
        <ScheduleNoticeModal
          isOpen={showScheduleModal}
          onClose={() => setShowScheduleModal(false)}
          exam={scheduleExam}
        />
      )}


    </div>
  );
};

export default ExamListView;
